// src/components/grammar/GrammarTopicCard.jsx
import { memo } from 'react'; // ✅ Importación moderna sin 'React'
import { CheckCircle2, Lightbulb, AlertCircle } from 'lucide-react';
import PropTypes from 'prop-types';

// --- CONSTANTES DE ESTILO (DISEÑO) ---
const STYLES = {
  CARD: "bg-white rounded-[2rem] p-6 md:p-8 shadow-xl border border-slate-100 relative overflow-hidden group hover:shadow-2xl transition-all duration-300",
  NUMBER_BG: "absolute -top-4 -right-4 text-9xl font-black text-slate-50 select-none group-hover:text-indigo-50/50 transition-colors",
  ICON_BUBBLE: "inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-700 font-black text-xl mb-4 shadow-inner",
  SECTION_EXAMPLES: "bg-emerald-50 rounded-[1.5rem] p-6 border-2 border-emerald-100/50",
  SECTION_TIPS: "bg-amber-50 rounded-[1.5rem] p-6 border-2 border-amber-100/50"
};

function GrammarTopicCard({ topic, index, formatText }) {
  return (
    <article className={STYLES.CARD}>
      {/* Decoración de fondo */}
      <div className={STYLES.NUMBER_BG} aria-hidden="true">
        {index + 1}
      </div>

      <header className="relative z-10 mb-6">
        <div className={STYLES.ICON_BUBBLE} aria-hidden="true">
          {index + 1}
        </div>
        <h3 className="text-2xl md:text-3xl font-black text-slate-800 leading-tight">
          {topic.title}
        </h3>
        <div className="mt-4 text-slate-600 leading-relaxed text-lg">
          {topic.content}
        </div>
      </header>

      <div className="relative z-10 space-y-6">
        {/* SECCIÓN DE EJEMPLOS */}
        {topic.examples?.length > 0 && (
          <section className={STYLES.SECTION_EXAMPLES} aria-labelledby={`examples-${index}`}>
            <h4 id={`examples-${index}`} className="flex items-center gap-2 text-emerald-700 font-black mb-4 text-xs uppercase tracking-widest">
              <CheckCircle2 size={18} aria-hidden="true" /> 
              Ejemplos Prácticos
            </h4>
            <ul className="space-y-4 text-slate-700 font-medium text-lg">
              {topic.examples.map((ex, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: formatText(ex) }} />
              ))}
            </ul>
          </section>
        )}

        {/* SECCIÓN DE CONSEJOS */}
        {topic.tips?.length > 0 && (
          <section className={STYLES.SECTION_TIPS} aria-labelledby={`tips-${index}`}>
            <h4 id={`tips-${index}`} className="flex items-center gap-2 text-amber-700 font-black mb-4 text-xs uppercase tracking-widest">
              <Lightbulb size={18} aria-hidden="true" /> 
              Consejo Pro
            </h4>
            <ul className="space-y-4">
              {topic.tips.map((tip, i) => (
                <li key={i} className="flex gap-3 text-slate-700 font-medium">
                  <div className="bg-amber-200 p-1 rounded-lg mt-1 shrink-0" aria-hidden="true">
                    <AlertCircle size={14} className="text-amber-700" />
                  </div>
                  <span dangerouslySetInnerHTML={{ __html: formatText(tip) }} />
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </article>
  );
}

// --- DOCUMENTACIÓN Y VALIDACIÓN ---
GrammarTopicCard.propTypes = {
  topic: PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    examples: PropTypes.arrayOf(PropTypes.string),
    tips: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  index: PropTypes.number.isRequired,
  formatText: PropTypes.func.isRequired
};

export default memo(GrammarTopicCard);